import { type FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from '@mint-lib/tw-components';

import { EntityStatuses } from '../../../constants/shared.js';
import StatusDetailsModalContent from './StatusDetailsModalContent.jsx';

type Props = {
  open: boolean;
  onClose: () => void;
  approvalProcessId: string | null;
  userStatus: EntityStatuses;
};

type InteractOutsideEvent = Parameters<
  NonNullable<React.ComponentProps<typeof DialogRoot>['onInteractOutside']>
>[0];

const StatusDetailsModal: FC<Props> = ({
  open,
  onClose,
  approvalProcessId,
  userStatus,
}) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit_v2');

  const handleModalOpenChange = (isOpened: boolean) => {
    if (isOpened) {
      return;
    }

    onClose();
  };

  const handleInteractOutside = (event: InteractOutsideEvent) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const isClickOnSnackbar = event.target.closest('[data-sonner-toast]');

    if (isClickOnSnackbar) {
      event.preventDefault();
    }
  };

  return (
    <Dialog modal onOpenChange={handleModalOpenChange} open={open}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot
          size="m"
          className="outline-none"
          aria-describedby={undefined}
          onInteractOutside={handleInteractOutside}
        >
          <DialogHeader>
            <DialogTitle>{t('Status details')}</DialogTitle>
          </DialogHeader>
          <DialogContent>
            <div className="w-full">
              <StatusDetailsModalContent
                isModalOpen={open}
                approvalProcessId={approvalProcessId}
                userStatus={userStatus}
              />
            </div>
          </DialogContent>
          <DialogFooter>
            <Button
              data-dd-action-name="Close the modal"
              size="large"
              variant="contained"
              color="primary"
              aria-label="Close the modal"
              onClick={onClose}
            >
              {t('Got it')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export default StatusDetailsModal;
